import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Plus, Trash2, Edit, MoveUp, MoveDown } from "lucide-react";

interface RoadmapItem {
  id: string;
  title: string;
  description: string | null;
  status: string;
  display_order: number;
  created_at: string;
}

interface RoadmapSetting {
  id: string;
  status: string;
  colour: string;
}

const STATUS_LABELS: Record<string, string> = {
  submitted: "Submitted",
  planned: "Planned",
  up_next: "Up Next",
  released: "Released",
};

const STATUS_ORDER = ["submitted", "planned", "up_next", "released"];

export default function RoadmapManagement() {
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [settings, setSettings] = useState<RoadmapSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("submitted");
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RoadmapItem | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "submitted",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [itemsResult, settingsResult] = await Promise.all([
        supabase
          .from("roadmap_items")
          .select("*")
          .order("display_order", { ascending: true }),
        supabase.from("roadmap_settings").select("*"),
      ]);

      if (itemsResult.data) {
        setItems(itemsResult.data);
      }

      if (settingsResult.data) {
        setSettings(settingsResult.data);
      }
    } catch (error) {
      console.error("Error fetching roadmap data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getColour = (status: string) => {
    const setting = settings.find((s) => s.status === status);
    return setting?.colour || "#6B7280";
  };

  const getItemsByStatus = (status: string) => {
    return items
      .filter((item) => item.status === status)
      .sort((a, b) => a.display_order - b.display_order);
  };

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Get the max display_order for the selected status
    const statusItems = getItemsByStatus(formData.status);
    const maxOrder = statusItems.length > 0 
      ? Math.max(...statusItems.map(i => i.display_order)) + 1 
      : 0;

    const { error } = await supabase.from("roadmap_items").insert({
      title: formData.title,
      description: formData.description || null,
      status: formData.status,
      display_order: maxOrder,
    });

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Item added successfully" });
      setAddDialogOpen(false);
      setFormData({ title: "", description: "", status: "submitted" });
      fetchData();
    }
  };

  const handleEditItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;

    const { error } = await supabase
      .from("roadmap_items")
      .update({
        title: formData.title,
        description: formData.description || null,
      })
      .eq("id", selectedItem.id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Item updated successfully" });
      setEditDialogOpen(false);
      setSelectedItem(null);
      fetchData();
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!confirm("Are you sure you want to delete this item?")) return;

    const { error } = await supabase.from("roadmap_items").delete().eq("id", id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Item deleted successfully" });
      fetchData();
    }
  };

  const handleStatusChange = async (itemId: string, newStatus: string) => {
    // Get max order in new status section
    const statusItems = getItemsByStatus(newStatus);
    const maxOrder = statusItems.length > 0 
      ? Math.max(...statusItems.map(i => i.display_order)) + 1 
      : 0;

    const { error } = await supabase
      .from("roadmap_items")
      .update({ status: newStatus, display_order: maxOrder })
      .eq("id", itemId);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      fetchData();
    }
  };

  const handleMoveItem = async (item: RoadmapItem, direction: "up" | "down") => {
    const statusItems = getItemsByStatus(item.status);
    const currentIndex = statusItems.findIndex((i) => i.id === item.id);
    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= statusItems.length) return;

    const targetItem = statusItems[targetIndex];

    // Swap display_order values
    await Promise.all([
      supabase
        .from("roadmap_items")
        .update({ display_order: targetItem.display_order })
        .eq("id", item.id),
      supabase
        .from("roadmap_items")
        .update({ display_order: item.display_order })
        .eq("id", targetItem.id),
    ]);

    fetchData();
  };

  const handleColourChange = async (status: string, colour: string) => {
    const { error } = await supabase
      .from("roadmap_settings")
      .update({ colour })
      .eq("status", status);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      fetchData();
    }
  };

  const openEditDialog = (item: RoadmapItem) => {
    setSelectedItem(item);
    setFormData({
      title: item.title,
      description: item.description || "",
      status: item.status,
    });
    setEditDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Colour Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Section Colours</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {STATUS_ORDER.map((status) => (
              <div key={status} className="space-y-2">
                <Label>{STATUS_LABELS[status]}</Label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={getColour(status)}
                    onChange={(e) => handleColourChange(status, e.target.value)}
                    className="w-10 h-10 rounded border cursor-pointer"
                  />
                  <Input
                    value={getColour(status)}
                    onChange={(e) => handleColourChange(status, e.target.value)}
                    className="font-mono text-sm"
                    maxLength={7}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Items Management */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Roadmap Items</CardTitle>
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Roadmap Item</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddItem} className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Description (optional)</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_ORDER.map((status) => (
                        <SelectItem key={status} value={status}>
                          {STATUS_LABELS[status]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full">
                  Add Item
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              {STATUS_ORDER.map((status) => (
                <TabsTrigger key={status} value={status}>
                  <span className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getColour(status) }}
                    />
                    {STATUS_LABELS[status]} ({getItemsByStatus(status).length})
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>

            {STATUS_ORDER.map((status) => (
              <TabsContent key={status} value={status}>
                {getItemsByStatus(status).length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No items in this section.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {getItemsByStatus(status).map((item, index) => (
                      <Card key={item.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h4 className="font-medium">{item.title}</h4>
                              {item.description && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {item.description}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Move buttons */}
                              <div className="flex flex-col gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6"
                                  disabled={index === 0}
                                  onClick={() => handleMoveItem(item, "up")}
                                >
                                  <MoveUp className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6"
                                  disabled={index === getItemsByStatus(status).length - 1}
                                  onClick={() => handleMoveItem(item, "down")}
                                >
                                  <MoveDown className="h-3 w-3" />
                                </Button>
                              </div>

                              {/* Status selector */}
                              <Select
                                value={item.status}
                                onValueChange={(v) => handleStatusChange(item.id, v)}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUS_ORDER.map((s) => (
                                    <SelectItem key={s} value={s}>
                                      {STATUS_LABELS[s]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              {/* Edit button */}
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => openEditDialog(item)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>

                              {/* Delete button */}
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleDeleteItem(item.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Roadmap Item</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditItem} className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <Button type="submit" className="w-full">
              Save Changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
